import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PersonAdd } from '@mui/icons-material';
import { ClearOutlined } from '@mui/icons-material';
import CachedIcon from '@mui/icons-material/Cached';
import SearchIcon from '@mui/icons-material/Search';
import { InputBase, IconButton, Box, Stack, TablePagination, Typography, Chip, Tooltip, Divider } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { createTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import moment from 'moment/moment';
import { Toast } from 'app/components';
import { CustomizedTable, Loader, StyledButton } from 'app/components/v2';
import {
  CustomButtonDiv,
  CustomTableContainer,
  CustomTableHeader,
  CustomSearchDiv,
  CustomButtonsContainer,
  CustomPageHeader,
  CustomTableHeading,
} from './style';
import { toggleReset } from 'app/store/features/notificationsSlice';
// import { setUser } from 'app/store/features/updateUserSlice';
import { DarkmodeColors, ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';
import { useSelector } from 'react-redux';

export function CustomPagination({ count }) {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Box
      sx={{
        borderTop: '1px solid #526380',
        width: '97%',
        paddingTop: '10px',
        margin: '0 0.7em 0 0.7em',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <TablePagination
        component='div'
        count={count}
        page={page}
        rowsPerPage={10}
        rowsPerPageOptions={[]}
        labelRowsPerPage={''}
        sx={{
          color: 'white',
          '& .MuiIconButton-root': {
            visibility: 'hidden',
          },
          '& .MuiTablePagination-actions': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
          },
          '& .MuiPagination-root': {
            fontSize: '0.6em',
            color: '#8F95B2',
          },
          '& .MuiTablePagination-select': {
            fontSize: '0.6em',
            fontWeight: 700,
            lineHeight: '24px',
            color: '#8F95B2',
            border: '1px solid #E2E3E6',
            borderRadius: '8px',
            padding: '5px 10px',
          },
          '&.MuiTablePagination-select option': {
            fontSize: '0.6em',
            fontWeight: 700,
            lineHeight: '24px',
            color: '#8F95B2',
            borderRadius: '8px',
          },
          '&.MuiSelect-root': {
            '& .MuiPaper-root': {
              backgroundColor: DarkmodeColors.appBar,
            },
          },
          '& .MuiSelect-icon': {
            color: 'white',
          },
        }}
      />
      <Pagination
        shape='rounded'
        variant='outlined'
        count={pageCount}
        page={page + 1}
        onChange={(event, value) => apiRef.current.setPage(value - 1)}
        sx={{
          '& .MuiPaginationItem-root': {
            border: 'none',
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: '0.6em',
            lineHeight: '24px',
            color: '#8F95B2',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
          '& .MuiPaginationItem-outlined': {
            borderRadius: '8px',
            border: '1px solid #E2E3E6',
            '&.Mui-selected': {
              border: 'none',
              backgroundColor: 'transparent',
              color: ColorsV2.white,
              fontSize: '1rem',
              fontWeight: 800,
            },
          },
          '& .Mui-selected': {
            border: 'none',
            color: ColorsV2.primary,
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: '0.6em',
            lineHeight: '24px',
          },
        }}
      />
    </Box>
  );
}

const Users = () => {
  const navigate = useNavigate();
  const [usersData, setUsersData] = useState([]);
  const [search, setSearch] = useState('');
  const [loader, setLoader] = useState(false);
  const [roles, setRoles] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [count, setCount] = useState(0);
  const [adm, setAdm] = useState('');
  const [open, setOpen] = useState(false);

  const { t } = useTranslation('translation', { keyPrefix: 'users_headings' });
  const location = useLocation();

  const state = location.state;
  const length = usersData.length;

  const accountSettings = useSelector((state) => state.updateUser.userData);

  const dispatch = useDispatch();

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        mobile: 0,
        tablet: 640,
        laptop: 1024,
        desktop: 1350,
      },
    },
  });

  const matchesSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const matchesMedium = useMediaQuery(theme.breakpoints.up('md'));
  const matchesLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const matchesExtraLarge = useMediaQuery(theme.breakpoints.up('xl'));
  const matchesLaptop = useMediaQuery(theme.breakpoints.up('laptop'));
  const matchesDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  const formatTime = array => {
    const formattedTimeArray = array.map(item => ({
      ...item,
      lastLogin:
        item.lastLogin !== ''
          ? moment(item.lastLogin, 'YYY-MM-DD h:mm:ss A').format('DD MMM YY HH:mm')
          : '',
    }));
    return formattedTimeArray;
  };

  const handleSearch = event => {
    setSearch(event.target.value);
  };

  const fetchUsers = async () => {
    setLoader(true);
    let nrOfUsers = 0;
  
    try {
      const usersRes = await apiService.getUsers();
  
      if (usersRes.data?.status === 'success') {
        const users = usersRes.data.data || [];
        setUsersData(users);
        nrOfUsers += users.length + 1; // Include current user
      } else {
        setUsersData([]);
      }
    } catch (error) {
      console.error(error);
      setUsersData([]);
    } finally {
      setLoader(false);
      setCount(nrOfUsers);
    }
  };

  const fetchRoles = async () => {
    const res = await apiService.getRoles();
    setRoles(res.data);
  };

  const fetchAuthorities = async () => {
    let res = null;
    try {
      res = await apiService.getAuthorities();
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setAuthorities(res.data);
    }
  }

  useEffect(() => {
    location.state?.message ? setOpen(true) : setOpen(false);
    fetchUsers();
    fetchRoles();
    fetchAuthorities();
    dispatch(toggleReset());
  }, []);

  useEffect(() => {
    const isAdmin = authorities.find(
      (auth) => auth.id === accountSettings.auth && auth.level >= 100
    );
    setAdm(isAdmin ? 'admin' : '');
  }, [accountSettings, authorities]);
  

  useEffect(() => {
    if(Object.keys(accountSettings)?.length <= 0 || usersData?.length <= 0) return;
    const formatedArray = formatTime([{...accountSettings, isSelf: true}, ...usersData]);
    setFilteredList(
      formatedArray.filter(item => {
        if (!item?.id) {
          return;
          // In early loading omitting this check will cause a fatal error
          // (Cause unclear - Possibly related to empty objects being passed through, causing issues with rowID when generating cells/rows)
          //
          // This is a temporary fix and should be returned to in the future to adress the root issue
          // The root issue is unclear but the current way data grid rendering is handled leaves room for errors (i.e when properties are missing)
          // The current implementation is also hard to follow and debug. Variable renaming would also be appropriate
        }
        return (
          item?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
          item?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
          item?.email?.toLowerCase().includes(search.toLowerCase()) ||
          item?.lastLogin?.toLowerCase().includes(search.toLowerCase())
        );
      })
    );
  }, [accountSettings, usersData, search]);

  const handleClick = params => {
    if(params.row?.isSelf) return;
    if (adm === 'admin') {
      // dispatch(setUser(params.row));
      navigate(`/edit-user/${params.row.id}`, {
        state: { id: params.row, title: 'Edit', addUser: 'edit-user' },
      });
    }
  };

  const columns = [
    {
      field: 'firstName',
      headerName: t('heading_1'),
      minWidth: matchesExtraLarge
        ? 270
        : matchesDesktop
        ? 270
        : matchesLarge
        ? 200
        : matchesLaptop
        ? 200
        : matchesMedium
        ? 200
        : matchesSmall
        ? 200
        : 200,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      renderCell: params => (
        <div
          onClick={() => {
            handleClick(params);
          }}
          style={{
            padding: '10px 0px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            color: ColorsV2.white,
            cursor: adm === 'admin' && !params.row?.isSelf ? 'pointer' : 'default',
            width: '100%',
          }}
        >
          {params.row.firstName !== '' && params.row.lastName !== '' ? (
            <Typography
              variant='body1'
              style={{
                background: `#${params.row.id.slice(-6)}`,
                color: ColorsV2.white,
                marginRight: '8px',
                marginLeft: '18px',
                padding: '0.4em',
                borderRadius: '50px',
                width: '2rem',
                height: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // fontSize: '1rem',
              }}
            >{`${params.row.firstName.charAt(0).toUpperCase()} ${params.row.lastName
              .charAt(0)
              .toUpperCase()}`}</Typography>
          ) : (
            <></>
          )}
          <Stack direction='column'>
          <Typography
            variant='body1'
            style={{
              marginLeft: '10px',
              // color: '#1C1D21',
              // fontSize: '1rem',
              // fontFamily: 'Roboto, sans-serif',
              // fontWeight: '700',
              color: ColorsV2.white,
            }}
          >{`${params.row.firstName} ${params.row.lastName}`}</Typography>
          {params.row.deletionRequested !== 0 && adm === 'admin' && !params.row.isSelf
          ?
          <Tooltip title={t('deletion_request_info')}>
            <Chip 
            label={t('deletion_request')} 
            sx={{ 
              color: ColorsV2.white, 
              bgcolor: ColorsV2.acent, 
              margin: '10px',
              cursor: 'pointer'
            }} /> 
            </Tooltip>
          : ''}
          </Stack>
        </div>
      ),
    },
    {
      field: 'email',
      headerName: t('heading_2'),
      minWidth: matchesExtraLarge
        ? 270
        : matchesDesktop
        ? 270
        : matchesLarge
        ? 200
        : matchesLaptop
        ? 200
        : matchesMedium
        ? 200
        : matchesSmall
        ? 200
        : 200,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      renderCell: params => (
        <Typography
          onClick={() => {
            handleClick(params);
          }}
          variant='body1'
          style={{
            padding: '0.4em 0px',
            color: ColorsV2.white,
            cursor: adm === 'admin' && !params.row?.isSelf ? 'pointer' : 'default',
            width: '100%',
            // marginLeft: '10px',
            // fontSize: '1rem',
            // fontFamily: 'Roboto, sans-serif',
          }}
        >
          {params.row.email}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: t('heading_3'),
      minWidth: matchesExtraLarge
        ? 270
        : matchesDesktop
        ? 270
        : matchesLarge
        ? 200
        : matchesLaptop
        ? 200
        : matchesMedium
        ? 200
        : matchesSmall
        ? 200
        : 200,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      renderCell: params => (
        <Typography
          onClick={() => {
            handleClick(params);
          }}
          variant='body1'
          style={{
            padding: '0.4em 0px',
            color: ColorsV2.white,
            cursor: adm === 'admin' && !params.row?.isSelf ? 'pointer' : 'default',
            width: '100%',
            // marginLeft: '10px',
            // fontSize: '1rem',
            // fontFamily: 'Roboto, sans-serif',
          }}
        >
          {roles.find(role => params?.value === role.id)?.name || t('role_not_found')}
        </Typography>
      ),
    },
    {
      field: 'lastLogin',
      headerName: t('heading_4'),
      minWidth: matchesExtraLarge
        ? 300
        : matchesDesktop
        ? 240
        : matchesLarge
        ? 200
        : matchesLaptop
        ? 150
        : matchesMedium
        ? 120
        : matchesSmall
        ? 90
        : 70,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      renderCell: params => (
        <Typography
          onClick={() => {
            handleClick(params);
          }}
          variant='body1'
          style={{
            padding: '0.4em 0px',
            color: ColorsV2.white,
            cursor: adm === 'admin' && !params.row?.isSelf ? 'pointer' : 'default',
            // marginLeft: '10px',
            // fontSize: '1rem',
            // fontFamily: 'Roboto, sans-serif',
            width: '100%',
          }}
        >
          {params.row.lastLogin ? (
            params.row.lastLogin
          ) : (
            <div style={{ width: '28%', display: 'flex', justifyContent: 'center' }}>---</div>
          )}
        </Typography>
      ),
    },
  ];

  return (
      <Box sx={{ width: '100%', marginTop: '7%', marginX: '10px', padding: '10px' }}>
        <CustomPageHeader paddingBottom={1}>
          <Typography variant='h4' sx={{ color: ColorsV2.white }}>{t('main')}</Typography>
          {adm === 'admin' && (
            <StyledButton
              name='IconButton'
              startIcon={<PersonAdd sx={{ fontSize: '1rem' }} />}
              onClick={() => {
                navigate('/add-user', { state: { addUser: 'add-user' } });
              }}
              sx={{ textTransform: 'uppercase' }}
            >
              <Typography variant='caption'>{t('addUser')}</Typography>
            </StyledButton>
          )}
        </CustomPageHeader>
        <Divider sx={{ borderColor: ColorsV2.outline }} />
        <CustomTableContainer>
          <CustomTableHeader>
            <CustomSearchDiv>
              <CustomTableHeading variant='subtitle1' sx={{ color: ColorsV2.white }}>
                {t('all')} ({length})
              </CustomTableHeading>
              <CustomButtonDiv
                sx={{
                  background: 'transparent',
                  border: `1px solid ${ColorsV2.white}`,
                  borderRadius: '5px',
                  height: '1.6em',
                  width: '13vw',
                  marginLeft: '10px',
                }}
              >
                <InputBase
                  sx={{
                    ml: 1,
                    flex: 1,
                    background: 'transparent',
                    color: 'white',
                    fontSize: '1rem',
                  }}
                  placeholder={t('search')}
                  inputProps={{ 'aria-label': 'search' }}
                  value={search}
                  onChange={event => handleSearch(event)}
                />
                {search === '' ? (
                  <IconButton type='button' sx={{ p: '8px' }} aria-label='search'>
                    <SearchIcon
                      sx={{
                        color: ColorsV2.white,
                        fontSize: '1rem',
                        '&:hover': { color: ColorsV2.acent },
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    type='button'
                    sx={{ p: '8px' }}
                    aria-label='search'
                    onClick={() => setSearch('')}
                  >
                    <ClearOutlined
                      sx={{
                        color: ColorsV2.white,
                        fontSize: '1rem',
                        '&:hover': { color: ColorsV2.acent },
                      }}
                    />
                  </IconButton>
                )}
              </CustomButtonDiv>
            </CustomSearchDiv>
            <CustomButtonsContainer>
              <IconButton
                type='button'
                aria-label='refresh'
                sx={{
                  border: `1px solid ${ColorsV2.white}`,
                  borderRadius: '5px',
                  height: '1.1em',
                  marginInline: '5px',
                  '&:hover': { bgcolor: ColorsV2.acent },
                }}
                onClick={() => {
                  fetchUsers();
                }}
              >
                {/* <img src='/assets/images/refresh-icon.svg' /> */}
                <CachedIcon sx={{ color: ColorsV2.white, fontSize: '1rem' }} />
              </IconButton>
            </CustomButtonsContainer>
          </CustomTableHeader>
          {!loader && (
            <CustomizedTable
              rows={filteredList}
              columns={columns}
              autoHeight
              sortingOrder={['desc', 'asc']}
              pagination
              disableColumnMenu
              pageSize={10}
              rowsPerPageOptions={[10]}
              rowHeight={70}
              headerHeight={50}
              hideFooterSelectedRowCount
              sx={{
                '&>.MuiDataGrid-main': {
                  '&>.MuiDataGrid-columnHeaders': {
                    paddingLeft: '30px',
                    fontSize: '1rem',
                  },

                  '& .MuiDataGrid-row': {
                    borderBottom: 'transparent',
                  },
                },
              }}
              components={{
                Footer: () => CustomPagination({ count }),
                NoRowsOverlay: () => (
                  <Stack
                    height='100%'
                    alignItems='center'
                    justifyContent='center'
                    sx={{ color: 'white', fontSize: '1rem' }}
                  >
                    {t('noRows')}
                  </Stack>
                ),
              }}
            />
          )}
          {loader && <Loader />}
          <Toast
            open={open}
            setOpen={setOpen}
            message={state?.message}
            severity={state?.severity}
          />
        </CustomTableContainer>
      </Box>
  );
};

export default Users;
